import React, { useCallback } from 'react';

import { Button, Tooltip, Typography } from 'antd';

import { Field, useFormikContext } from 'formik';
import DashboardSegmented from 'v2/components/Pages/Dashboard/DashboardSegmented';
import { SelectFormField } from 'v2/components/Shared/FormFields/SelectFormField';
import { SelectProduct } from 'v2/components/Shared/FormFields/SelectProduct';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import UrlValidationFormField from 'v2/components/Shared/UrlValidationFormField';
import { WebhookContentTypeAlias, WebhookTypes } from 'v2/interfaces/webhook2';
import type { FieldsWebHook } from 'v2/interfaces/webhook2';

import { Icon } from '@eduzz/ui-antd-theme';

import { useWebhookContext } from '../context';

enum ProductSelectType {
  all = 'all',
  custom = 'custom'
}

export const ALL_PRODUCTS_ID = -1;

const MOZ_CONTENT_TYPE_URL = 'https://developer.mozilla.org/pt-BR/docs/Web/HTTP/Headers/Content-Type';

const Fields = React.memo(() => {
  const { setSelectedWebhook, setIsFormOpen } = useWebhookContext();
  const { isSubmitting, isValid, values, setValues } = useFormikContext<FieldsWebHook>();
  const [isUrlValidated, setIsUrlValidated] = React.useState(false);
  const [resetValidation, setResetValidation] = React.useState(0);

  let defaultProductSelectType = null;

  if (values.product_id.length) {
    defaultProductSelectType = values.product_id.includes(ALL_PRODUCTS_ID)
      ? ProductSelectType.all
      : ProductSelectType.custom;
  }

  const [productSelectType, setProductSelectType] = React.useState<ProductSelectType>(defaultProductSelectType);
  const [customSelectedProducts, setCustomSelectedProducts] = React.useState<number[]>([]);

  const setCustomProductSelectType = () => {
    setProductSelectType(ProductSelectType.custom);
    setValues({ ...values, product_id: customSelectedProducts });
  };

  const setAllProductSelectType = () => {
    setProductSelectType(ProductSelectType.all);

    if (!customSelectedProducts.includes(ALL_PRODUCTS_ID)) {
      setCustomSelectedProducts(values.product_id);
      setValues({ ...values, product_id: [ALL_PRODUCTS_ID] });
    }
  };

  const onClose = React.useCallback(() => {
    setSelectedWebhook(null);
    setIsFormOpen(false);
  }, [setIsFormOpen, setSelectedWebhook]);

  const editing = !!values?._id;

  const unvalidateOnChange = useCallback(() => {
    setResetValidation(prev => prev + 1);
    setIsUrlValidated(false);
  }, []);

  const handleProductSelectTypeChange = (value: ProductSelectType) => {
    if (value === ProductSelectType.all) {
      setAllProductSelectType();
      return;
    }

    setCustomProductSelectType();
  };

  return (
    <div className='flex flex-col gap-4'>
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
        <Field id='description' name='description' label='Nome da configuração *' fullWidth component={TextFormField} />

        <Field
          id='type'
          name='type'
          label='Tipo (evento) *'
          disabled={editing}
          fullWidth
          options={WebhookTypes}
          component={SelectFormField}
        />
      </div>

      <div className='flex flex-col gap-3 rounded-(--uizz-antd-border-radius-lg) bg-(--uizz-antd-color-fill-tertiary) p-4'>
        <div className='flex items-center gap-1'>
          <Typography.Text>Os eventos de quais produtos você deseja receber?</Typography.Text>
          <Tooltip title='Caso algum produto seja cadastrado futuramente em sua conta, você receberá os webhooks relacionados aos novos produtos também'>
            <span className='inline-flex cursor-help'>
              <Icon.CircleHelp size={14} className='text-(--uizz-antd-color-text-secondary)' />
            </span>
          </Tooltip>
        </div>

        <DashboardSegmented
          value={productSelectType || undefined}
          onChange={value => handleProductSelectTypeChange(value as ProductSelectType)}
          options={[
            { value: ProductSelectType.all, label: 'Todos os produtos' },
            { value: ProductSelectType.custom, label: 'Produtos específicos' }
          ]}
        />

        {productSelectType === ProductSelectType.custom && (
          <Field
            id='product_id'
            name='product_id'
            endpoint='/webhook/products'
            single={false}
            fullWidth
            component={SelectProduct}
            selected={values.product_id}
          />
        )}
      </div>

      <Field
        id='content_type'
        name='content_type'
        label='Formato de envio *'
        helperText={
          <>
            Cabeçalho Content Type. Para saber mais{' '}
            <Typography.Link href={MOZ_CONTENT_TYPE_URL} target='_blank' rel='noreferrer'>
              clique aqui.
            </Typography.Link>
          </>
        }
        fullWidth
        options={[
          { label: 'JSON', value: WebhookContentTypeAlias.json },
          { label: 'URL Encoded', value: WebhookContentTypeAlias.urlencoded }
        ]}
        component={SelectFormField}
        onChange={unvalidateOnChange}
      />

      <Field
        id='url'
        name='url'
        label='URL para envio de dados *'
        fullWidth
        component={UrlValidationFormField}
        resetValidation={resetValidation}
        helperText='Insira uma URL válida, obrigatório http:// ou https://'
        onValidateUrl={setIsUrlValidated}
      />

      <div className='flex flex-wrap items-center justify-end gap-2 pt-2'>
        <Button type='text' disabled={isSubmitting} onClick={onClose}>
          Cancelar
        </Button>

        <Tooltip
          placement='top'
          title={!isUrlValidated && isValid ? 'Sua URL precisa ser validada e retornar status 200 para salvar' : ''}
        >
          <span>
            <Button
              disabled={isSubmitting || !isValid || !isUrlValidated}
              htmlType='submit'
              type='primary'
              loading={isSubmitting}
            >
              Salvar
            </Button>
          </span>
        </Tooltip>
      </div>
    </div>
  );
});

export default Fields;
